import React from 'react';
import { CheckCircle2, AlertTriangle, Calendar } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';

export interface TestResult {
  test_id: string | null;
  method: string | null;
  type: string | null;
  date: string | null;
  organism: string | null;
  results: string | null;
  comments: string | null;
}

export interface Treatment {
  treatment_id: string | null;
  date: string | null;
  order: string | null;
  comments: string | null;
}

export interface Isolation {
  required: boolean;
  type: string | null;
  no_treatments_ordered: boolean;
  comments: string | null;
}

export interface ActiveInfectionData {
  name: string;
  identifiedDate: string;
  isolation: Isolation | null;
  signs: string[];
  tests: TestResult[];
  treatments: Treatment[];
  inCarePlan: boolean;
}

interface ActiveInfectionCardProps {
  infection: ActiveInfectionData;
  isNewInsight?: boolean;
}

export const ActiveInfectionCard: React.FC<ActiveInfectionCardProps> = ({ infection, isNewInsight }) => {
  return (
    <div className="border border-red-300 bg-white rounded-lg p-4">
      {/* Header: Name + Date on left, ISOLATION badge on right */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-bold text-[#B02828]">
              {infection.name}
            </h3>
            {isNewInsight && (
              <span className="inline-flex items-center text-[10px] font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                New
              </span>
            )}
          </div>
          <p className="text-xs text-gray-600 flex items-center gap-1">
            <Calendar className="w-3 h-3 shrink-0" />
            Identified: {formatDate(infection.identifiedDate)}
          </p>
        </div>
        {infection.isolation?.required && (
          <div className="text-right">
            <span className="inline-flex items-center gap-1 border border-[#B02828] text-[#B02828] font-semibold text-[11px] px-2.5 py-1 rounded-full whitespace-nowrap shrink-0 mb-1">
              <AlertTriangle className="w-3 h-3" />
              ISOLATION
            </span>
            {infection.isolation.type && (
              <p className="text-[10px] text-gray-600">{infection.isolation.type}</p>
            )}
          </div>
        )}
      </div>

      {/* Two-Column Layout: Signs & Symptoms | Test Results */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        {/* Left Column: Signs & Symptoms */}
        <div>
          <h4 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-2">
            SIGNS & SYMPTOMS
          </h4>
          {infection.signs.length > 0 ? (
            <ul className="space-y-1">
              {infection.signs.map((sign, index) => (
                <li key={index} className="text-sm text-gray-800 flex items-start">
                  <span className="mr-2 text-[#B02828]">&#8226;</span>
                  <span>{sign}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 italic">No symptoms recorded</p>
          )}
        </div>

        {/* Right Column: Test Results */}
        <div>
          <h4 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-2">
            TEST RESULTS ({infection.tests.length})
          </h4>
          {infection.tests.length > 0 ? (
            <div className="space-y-2">
              {infection.tests.map((test, idx) => (
                <div key={test.test_id || idx} className="text-sm border-l-2 border-gray-300 pl-2">
                  <p className="font-semibold text-gray-800">
                    {test.method}{test.type ? ` (${test.type})` : ""}
                  </p>
                  {test.date && (
                    <p className="text-xs text-gray-600">Date: {formatDate(test.date)}</p>
                  )}
                  {test.organism && (
                    <p className="text-xs text-gray-800">Organism: {test.organism}</p>
                  )}
                  {test.results && (
                    <p className="text-xs text-gray-800">Result: {test.results}</p>
                  )}
                  {test.comments && (
                    <p className="text-xs text-gray-600 italic">{test.comments}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No test results available</p>
          )}
        </div>
      </div>

      {/* Treatments Section */}
      <div className="mb-4 pt-3 border-t border-gray-200">
        <h4 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-2">
          ORDERS FOR TREATMENT ({infection.treatments.length})
        </h4>
        {infection.treatments.length > 0 ? (
          <ul className="space-y-1.5">
            {infection.treatments.map((treatment, idx) => (
              <li key={treatment.treatment_id || idx} className="text-sm text-gray-800 flex items-start gap-2">
                <span className="text-[#B02828] font-bold">•</span>
                <div className="flex-1">
                  {treatment.date && (
                    <span className="text-xs text-gray-600 mr-2">{formatDate(treatment.date)}:</span>
                  )}
                  <span className="font-medium">{treatment.order}</span>
                  {treatment.comments && (
                    <p className="text-xs text-gray-600 italic mt-0.5">{treatment.comments}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 italic">No treatment orders recorded</p>
        )}
      </div>

      {/* Footer - Care Plan Status (Hidden until feature implemented) */}
      {false && (
        <div className="flex items-center pt-3 border-t border-gray-200">
          {infection.inCarePlan ? (
            <div className="flex items-center gap-1.5 text-[#23833F]">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-xs font-medium">In Care Plan</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-[#B02828]">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-medium">Not Care Planned</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
